import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subscription } from 'rxjs';
import { JobService, ScannerService, OrderService, BatchService } from 'app/services';
import { Job } from 'app/models';
import { STATIONARY, QUEUED, HANDHELD, PACKING, COMPLETED } from 'ngrx/status-types';
var bwipjs = require('bwip-js');

@Component({
  templateUrl: 'start-printing-modal.component.html',
  styleUrls:   ['start-printing-modal.component.scss']
})
export class StartPrintingModal implements OnInit {
  @Input('batches') batches: any[] = [];
  currentBatch: any;
  nextBatch: any;
  nextBatchSpecialty: string = '';
  currentBatchJobs: Job[] = [];
  currentIndex = 0;
  currentJob: Job;
  currentBatchIndex = 0;
  currentBatchTotalJobs: number = 0;
  currentBatchProcessed: number = 0;
  validationError = '';
  validationOk = false;
  barcodeDataUrl: string = '';
  barcodeText: string = '';
  expectedSubAssembly: string = '';
  expectedSpecialty: string = '';
  expectedCartridgeUnit: string = '';
  expectedCartridgeUnitDisplay: string = '';
  inputSubAssembly: string = '';
  inputSpecialty: string = '';
  subassemblyScannedOk: boolean = false;
  subassemblyInvalid: boolean = false;
  specialtyScannedOk: boolean = false;
  specialtyInvalid: boolean = false;
  inputCartridge: string = '';
  cartridgeScannedOk: boolean = false;
  cartridgeInvalid: boolean = false;
  initSubscriptions: Subscription[] = [];
  mode: 'scan' | 'print' = 'scan';
  isPrintedUpdating: boolean = false;
  
  private lastScannedCartridgeUnitNormalized: string = '';
  private lastScannedSpecialtyNormalized: string = '';
  private scanActivityEntries: Array<{ activity: string; note: string; timestamp: string; user: string }> = [];
  private horizonIdFilter: string | null = null;

  constructor(
    public bsModalRef: BsModalRef, 
    private jobService: JobService,
    private scanner: ScannerService,
    private orderService: OrderService,
    private batchService: BatchService
  ) {}

  ngOnInit(): void {
    this.scanner.setExclusiveMode(true);
    // Ensure batches are processed by highest totalQty first
    this.batches = (this.batches || []).slice().sort((a, b) => {
      const qa = (a && a.totalQty) || 0;
      const qb = (b && b.totalQty) || 0;
      return qb - qa;
    });
    this.currentBatchIndex = 0;
    this.currentBatch = (this.batches && this.batches.length) ? this.batches[this.currentBatchIndex] : null;
    this.nextBatch = (this.batches && this.batches.length > 1) ? this.batches[this.currentBatchIndex + 1] : null;
    this.lastScannedSpecialtyNormalized = '';
    this.lastScannedCartridgeUnitNormalized = '';
    this.mode = 'scan';
    this.currentBatchTotalJobs = this.computeBatchJobCount(this.currentBatch);
    this.currentBatchProcessed = 0;
    // Establish an optional horizon filter if the initial list is from a single horizon
    if (this.batches && this.batches.length) {
      const horizonSet = new Set((this.batches || []).map(b => b && b.horizon_id).filter(Boolean));
      this.horizonIdFilter = horizonSet.size === 1 ? (this.batches[0] && this.batches[0].horizon_id) : null;
    }
    if (this.currentBatch) {
      this.loadJobsForBatch(this.currentBatch);
    }
    if (this.nextBatch) {
      this.nextBatchSpecialty = this.extractSpecialtyFromCompKey(this.nextBatch.compKey || '');
    }

    this.initSubscriptions = [
      this.scanner
        ._printingScanListener
        .subscribe(id => {
          const ID = id.replace(/[^A-Za-z0-9_]/g, '');
          const [l, t] = this.scanner._triggers;
          const location = this.scanner._locations[l];
          const type = this.scanner._types[t];

          console.log(`PTO Printing Scan Code ${ID} scanned at ` + location);
          //Example : 103-510-245 735-948 
          if (location === 'batch_cutting' && type === HANDHELD) {
            console.log('PTO Printing Scan Code Scanned successfully');

            const scanned = (ID || '').toUpperCase();
            const expectedSubassembly = ((this.currentJob && (this.currentJob as any).subAssembly) || '').toUpperCase();
            const expectedSpecialty = ((this.currentJob && (this.currentJob as any).oaDetails && (this.currentJob as any).oaDetails.pto && (this.currentJob as any).oaDetails.pto.pto_specialty_inks) || '')
              .replace(/\b(versafire)\b/gi, '')
              .replace(/\s+/g, '')
              .toUpperCase()
              .trim();
            const expectedCartridge = ((expectedSpecialty || '') + '').replace(/\s+/g, '').toUpperCase() + '_INK_UNIT';

            // Match any section in any order; if no match, mark all remaining as invalid
            let matched = false;
            if (!this.subassemblyScannedOk && scanned === expectedSubassembly) {
              this.subassemblyScannedOk = true;
              this.subassemblyInvalid = false;
              matched = true;
              this.recordScanResult('SUBASSEMBLY', scanned, true);
            }
            if (!this.specialtyScannedOk && scanned === expectedSpecialty) {
              this.specialtyScannedOk = true;
              this.specialtyInvalid = false;
              matched = true;
              this.recordScanResult('SPECIALTY INK', scanned, true);
              // Remember for subsequent jobs in this batch
              this.lastScannedSpecialtyNormalized = expectedSpecialty;
            }
            if (!this.cartridgeScannedOk && scanned === expectedCartridge) {
              this.cartridgeScannedOk = true;
              this.cartridgeInvalid = false;
              matched = true;
              this.recordScanResult('CARTRIDGE INK UNIT', scanned, true);
              // Remember for subsequent jobs in this batch
              this.lastScannedCartridgeUnitNormalized = expectedCartridge;
            }
            if (!matched) {
              // Mark all not-yet-validated sections invalid
              if (!this.subassemblyScannedOk) this.subassemblyInvalid = true;
              if (!this.specialtyScannedOk) this.specialtyInvalid = true;
              if (!this.cartridgeScannedOk) this.cartridgeInvalid = true;
              this.recordScanResult('UNKNOWN', scanned, false);
            }
          }
        })
    ];

    // Subscribe to live list of PRINTING batches; if a new higher-qty batch arrives,
    // merge into remaining queue and re-sort by totalQty desc, keeping current batch intact.
    const printingListSub = this.batchService
      .getPTOPrintingBatchList()
      .subscribe((list: any[]) => {
        if (!Array.isArray(list) || !this.batches || !this.batches.length) return;
        // Respect horizon filter if present
        const eligible = list.filter(b => {
          if (!b) return false;
          if (this.horizonIdFilter) return b.horizon_id === this.horizonIdFilter;
          return true;
        });
        // Determine processed/current range
        const processedAndCurrent = new Set((this.batches.slice(0, (this.currentBatchIndex || 0) + 1) || []).map(b => b && b.id));
        const remaining = this.batches.slice((this.currentBatchIndex || 0) + 1) || [];
        const remainingIds = new Set(remaining.map(b => b && b.id));
        // Add new candidates not already queued and not processed/current
        const additions = eligible.filter(b => !!b && !processedAndCurrent.has(b.id) && !remainingIds.has(b.id) && (!!b.numberOfJobs && b.numberOfJobs > 0));
        if (!additions.length) {
          // Nothing new; still consider re-sorting remaining by latest totals if list changed
          const latestById: { [id: string]: any } = {};
          eligible.forEach(b => { latestById[b.id] = b; });
          const updatedRemaining = remaining.map(b => latestById[b.id] || b);
          const sortedRemaining = updatedRemaining.slice().sort((a, b) => {
            const qa = (a && a.totalQty) || 0;
            const qb = (b && b.totalQty) || 0;
            return qb - qa;
          });
          // Only update if order changed materially
          const changed = sortedRemaining.length !== remaining.length || sortedRemaining.some((b, i) => (remaining[i] && remaining[i].id) !== (b && b.id));
          if (changed) {
            this.batches = [
              ...this.batches.slice(0, (this.currentBatchIndex || 0) + 1),
              ...sortedRemaining
            ];
            this.nextBatch = sortedRemaining[0] || null;
            this.nextBatchSpecialty = this.nextBatch ? this.extractSpecialtyFromCompKey(this.nextBatch.compKey || '') : '';
          }
          return;
        }
        // Merge additions with remaining and re-sort by totalQty desc
        const mergedRemaining = [ ...remaining, ...additions ].slice().sort((a, b) => {
          const qa = (a && a.totalQty) || 0;
          const qb = (b && b.totalQty) || 0;
          return qb - qa;
        });
        this.batches = [
          ...this.batches.slice(0, (this.currentBatchIndex || 0) + 1),
          ...mergedRemaining
        ];
        // Update next batch reference
        this.nextBatch = mergedRemaining[0] || null;
        this.nextBatchSpecialty = this.nextBatch ? this.extractSpecialtyFromCompKey(this.nextBatch.compKey || '') : '';
      });
    this.initSubscriptions.push(printingListSub);
    
  }

  async markPrintedAndNext(): Promise<void> {
    this.isPrintedUpdating = true;
    try {
      const timestamp  = new Date().getTime();
      let user = (localStorage.getItem('currentUser') && JSON.parse(localStorage.getItem('currentUser')) && JSON.parse(localStorage.getItem('currentUser')).email) || 'PTO Wizard';
      let batchIdHistory = this.currentJob && (this.currentJob as any).batchIdHistory ? [ ...(this.currentJob as any).batchIdHistory ] : [];
      const curBatchId = (this.currentJob as any).batchId;
      if (curBatchId && batchIdHistory.indexOf(curBatchId) === -1) {
        batchIdHistory = [ curBatchId, ...batchIdHistory ];
      }
      // If MTS card, move directly to PACKING after printing; otherwise QUEUED
      const makeToStock = !!((this.currentJob as any).makeToStock);
      const newStatus = makeToStock ? PACKING : QUEUED;
      const updatedJob = Object.assign({}, this.currentJob, {
        activity_history: [
          // Include scan results captured during the scan phase (older than the print mark)
          ...this.scanActivityEntries,
          {
            activity : newStatus,
            note     : `${makeToStock ? 'PTO Cardstock - printed and ready to pack' : 'PTO Printed'}`,
            timestamp: `${timestamp}`,
            user
          },
          ...((this.currentJob as any).activity_history || [])
        ],
        previous_status: (this.currentJob as any).status,
        status         : newStatus,
        batchId        : null,
        batchIdHistory
      });
      await this.orderService.updateJobAsync(updatedJob);

      // Remove job from current batch and persist
      const updatedBatch = this.batchService.removeBatchedJob(this.currentBatch, this.currentJob.id);
      // If this was the last job in the batch, mark batch completed and designatedScrap
      // if (!updatedBatch.numberOfJobs || updatedBatch.numberOfJobs === 0) {
      //   const completedEntry = {
      //     activity : COMPLETED,
      //     note     : COMPLETED,
      //     timestamp: `${timestamp}`,
      //     user
      //   };
      //   const activity_history = (updatedBatch as any).activity_history ? [ completedEntry, ...((updatedBatch as any).activity_history || []) ] : [ completedEntry ];
      //   (updatedBatch as any).batchStatus = COMPLETED;
      //   (updatedBatch as any).designatedScrap = true;
      //   (updatedBatch as any).activity_history = activity_history;
      // }
      this.batchService.updateBatch(updatedBatch);
      this.currentBatch = updatedBatch;
      // sync local batches array
      if (this.batches && this.batches.length > this.currentBatchIndex) {
        this.batches[this.currentBatchIndex] = updatedBatch;
      }
    } catch (e) {
      console.error('Failed to update job to QUEUED:', e);
    } finally {
      // If current batch finished, advance to next available batch
      if (!this.currentBatch || !this.currentBatch.numberOfJobs) {
        const advanced = this.advanceToNextBatch();
        if (!advanced) {
          this.isPrintedUpdating = false;
          this.close();
          return;
        }
      } else {
        // increment processed counter after successful updates
        this.currentBatchProcessed = this.currentBatchProcessed + 1;
        // Reload jobs for the current batch and restart scanning
        this.loadJobsForBatch(this.currentBatch);
        this.mode = 'scan';
      }
      this.isPrintedUpdating = false;
    }
  }

  private advanceToNextBatch(): boolean {
    this.currentBatchIndex = (this.currentBatchIndex || 0) + 1;
    while (this.batches && this.currentBatchIndex < this.batches.length) {
      const candidate = this.batches[this.currentBatchIndex];
      if (candidate && candidate.numberOfJobs && candidate.numberOfJobs > 0) {
        this.currentBatch = candidate;
        this.nextBatch = (this.batches.length > this.currentBatchIndex + 1) ? this.batches[this.currentBatchIndex + 1] : null;
        // Reset remembered scans for a new batch
        this.lastScannedSpecialtyNormalized = '';
        this.lastScannedCartridgeUnitNormalized = '';
        if (this.nextBatch) {
          this.nextBatchSpecialty = this.extractSpecialtyFromCompKey(this.nextBatch.compKey || '');
        } else {
          this.nextBatchSpecialty = '';
        }
        this.currentBatchTotalJobs = this.computeBatchJobCount(this.currentBatch);
        this.currentBatchProcessed = 0;
        this.loadJobsForBatch(this.currentBatch);
        this.mode = 'scan';
        return true;
      }
      this.currentBatchIndex += 1;
    }
    // no more batches
    return false;
  }

  ngOnDestroy(): void {
    console.log("Destroying...");
    this.scanner.setExclusiveMode(false);
    this.initSubscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private loadJobsForBatch(batch: any): void {
    this.jobService.getJobList()
      .take(1)
      .subscribe((jobs: Job[]) => {
        const jobIDs: string[] = [];
        if (batch && batch.jobs) {
          batch.jobs.forEach(bucket => {
            bucket.jobsList && bucket.jobsList.forEach(j => jobIDs.push(j.jobId));
          });
        }
        // Order the jobs exactly as they appear in the batch's jobsList sequence
        this.currentBatchJobs = jobIDs
          .map(id => jobs.find(j => j.id === id))
          .filter(j => !!j) as Job[];
        this.currentIndex = 0;
        this.currentJob = this.currentBatchJobs[0];
        this.prepareCurrentJob();
      });
  }

  private computeBatchJobCount(batch: any): number {
    if (!batch || !batch.jobs) return 0;
    let total = 0;
    batch.jobs.forEach(bucket => {
      total += (bucket.jobsList && bucket.jobsList.length) || 0;
    });
    return total;
  }

  private prepareCurrentJob(): void {
    this.validationError = '';
    this.validationOk = false;
    this.mode = 'scan';
    this.inputSubAssembly = '';
    this.inputSpecialty = '';
    this.subassemblyScannedOk = false;
    this.subassemblyInvalid = false;
    this.specialtyScannedOk = false;
    this.specialtyInvalid = false;
    this.inputCartridge = '';
    this.cartridgeScannedOk = false;
    this.cartridgeInvalid = false;
    this.scanActivityEntries = [];

    if (!this.currentJob) { return; }

    let expectedSubAssembly: any = (this.currentJob as any).subAssembly || null;
    if (!expectedSubAssembly) {
      const anyJob: any = this.currentJob;
      expectedSubAssembly = anyJob && anyJob.oaDetails && anyJob.oaDetails.material && anyJob.oaDetails.material.subassembly_part_number;
    }
    this.expectedSubAssembly = (expectedSubAssembly || '').toString();

    let specialty = '';
    const anyJob: any = this.currentJob;
    if (anyJob && anyJob.oaDetails && anyJob.oaDetails.pto && anyJob.oaDetails.pto.pto_specialty_inks) {
      specialty = (anyJob.oaDetails.pto.pto_specialty_inks || '')
        .replace(/\b(versafire)\b/gi, '')
        .replace(/\s+/g, '')
        .trim();
    }
    this.expectedSpecialty = specialty;
    this.expectedCartridgeUnit = ((this.expectedSpecialty || '') + '').replace(/\s+/g, '').toUpperCase() + '_INK_UNIT';
    this.expectedCartridgeUnitDisplay = this.expectedCartridgeUnit.replace(/_/g, ' ');

    // Bypass specialty and cartridge scans when specialty is CMYK
    const isCMYK = (this.expectedSpecialty || '').trim().toUpperCase() === 'CMYK';
    if (isCMYK) {
      this.specialtyScannedOk = true;
      this.cartridgeScannedOk = true;
      // Treat CMYK as already remembered for subsequent jobs
      this.lastScannedSpecialtyNormalized = this.normalizeSpecialty(this.expectedSpecialty);
      this.lastScannedCartridgeUnitNormalized = this.normalizeCartridge(this.expectedCartridgeUnit);
    } else {
      // If expected values match the last successfully scanned values in this batch, bypass scans
      const expectedSpecialtyNorm = this.normalizeSpecialty(this.expectedSpecialty);
      const expectedCartridgeNorm = this.normalizeCartridge(this.expectedCartridgeUnit);
      //For now, everything is scanned every time
      // if (expectedSpecialtyNorm && expectedSpecialtyNorm === this.lastScannedSpecialtyNormalized) {
      //   this.specialtyScannedOk = true;
      // }
      // if (expectedCartridgeNorm && expectedCartridgeNorm === this.lastScannedCartridgeUnitNormalized) {
      //   this.cartridgeScannedOk = true;
      // }
    }

    const orderId = (this.currentJob as any).orderId || '';
    const qty = (this.currentJob as any).qty || '';
    // this.barcodeText = `${orderId}_${this.currentJob.id}_${qty}_${this.expectedSubAssembly}_${this.expectedSpecialty}`; //disabled for now since only brings up printfile when WO is needed as well
    this.barcodeText = `${orderId}_${this.currentJob.id}`;

    try {
      const canvas = document.createElement('canvas');
      bwipjs.toCanvas(canvas, {
        bcid: 'datamatrix',
        text: this.barcodeText,
        scale: 3,
        includetext: false,
      });
      this.barcodeDataUrl = canvas.toDataURL('image/png');
    } catch (e) {
      console.log(`Datamatrix generation Error : ${e}`);
      this.barcodeDataUrl = '';
    }
  }

  private extractSpecialtyFromCompKey(compKey: string): string {
    if (!compKey) { return ''; }
    const upper = String(compKey).toUpperCase();
    const idx = upper.indexOf('_PTO_');
    if (idx === -1) { return ''; }
    const after = compKey.substring(idx + 5);
    const tokens = after.split('_').filter(Boolean);
    const filtered = tokens.filter(t => {
      const v = t.toLowerCase();
      if (v === 'versafire' || v === 'printing' || v === 'cutting' || v === 'cardstock') return false;
      if (/^\d+$/.test(v)) return false;
      if (/^\d{2}-\d{2}-\d{4}$/.test(v)) return false;
      return true;
    });
    const ink = filtered.length ? filtered[filtered.length - 1] : '';
    return ink.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  }

  private normalizeSpecialty(value: string): string {
    return (value || '')
      .replace(/\b(versafire)\b/gi, '')
      .replace(/\s+/g, '')
      .toUpperCase()
      .trim();
  }

  private normalizeCartridge(value: string): string {
    return (value || '')
      .replace(/\s+/g, '')
      .toUpperCase()
      .trim();
  }


  startPrinting(): void {
    // Toggle to print view first; on subsequent click, advance to next job
    if (this.mode === 'scan') {
      this.mode = 'print';
      return;
    } else {
      // Proceed to next job
      this.validationOk = false;
      this.validationError = '';
      this.currentIndex += 1;
      if (this.currentIndex >= this.currentBatchJobs.length) {
        this.close();
        return;
      }
      this.currentJob = this.currentBatchJobs[this.currentIndex];
      this.prepareCurrentJob();
    }
  }

  private recordScanResult(
    section: 'SUBASSEMBLY' | 'SPECIALTY INK' | 'CARTRIDGE INK UNIT' | 'UNKNOWN',
    scanned: string,
    ok: boolean,
  ): void {
    const timestamp = new Date().getTime();
    const user = (localStorage.getItem('currentUser') && JSON.parse(localStorage.getItem('currentUser')) && JSON.parse(localStorage.getItem('currentUser')).email) || 'PTO Wizard';
    const activity = ok ? 'PTO_SCAN' : 'PTO_SCAN_INVALID';
    const parts: string[] = [];
    parts.push(`${section}`);
    if (scanned) parts.push(`Scanned: ${scanned}`);
    const note = parts.join(' ');
    this.scanActivityEntries = [
      {
        activity,
        note,
        timestamp: `${timestamp}`,
        user
      },
      ...this.scanActivityEntries
    ];
  }


  close(): void {
    !!this.bsModalRef && this.bsModalRef.hide();
  }
}





// WEBPACK FOOTER //
// /Users/jlam2/impresocloud/www/client/src/app/shared/modals/start-printing-modal/start-printing-modal.component.ts